/**
 * Fetches the *entire* set of rescue devices for a given filter (not just one
 * page), draining every server page up front so the caller can sort and
 * paginate the full list client-side.
 *
 * This is the opt-in counterpart to `useRescueDevices`, which pages on demand.
 * The Activated section normally uses the server-paginated hook; it switches to
 * this drained hook only once the user activates a column sort (a full-dataset
 * sort can't be done from a single server page). Pass `skip: true` to keep the
 * query dormant until then — no network cost while the section is unsorted.
 *
 * The page-drain bookkeeping is shared with the Unassigned inventory and Pending
 * adoptions sections via `useDrainAllPages`.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import type { Device } from '../lib/types';
import type { RescueDevicesData, RescueDeviceFilter } from '../lib/rescueQueries';
import { rescueDevicesQuery } from '../lib/rescueQueries';
import { mapRescueDeviceItemToDevice } from '../lib/deviceAdapters';
import useDrainAllPages from './useDrainAllPages';

/**
 * Page size for the drain loop. Matches the server's own clamp for this query
 * (MAX_LIMIT = 50 in rescueDeviceStore) — asking for more just gets clamped
 * down, so we request exactly what a page returns rather than a misleading
 * larger number.
 */
const DRAIN_PAGE_SIZE = 50;

/**
 * Whether a live query's `input` is one of this hook's drains rather than a paginated
 * `useRescueDevices` on the same filter — both can be mounted at once, and a mutation that
 * wants to spare the drain from `refetchQueries: 'active'` has to tell them apart.
 *
 * A drain accumulates page after page through `fetchMore` and never re-anchors, so it sends
 * no `offset`; anything paginating always sends one. That is the distinction, rather than
 * page size, which either could plausibly share.
 */
export function isDrainedDevicesQuery(
  // Open-ended: a real `input` also carries `limit` and, when searching, `query`. Only the
  // two fields that identify it are named.
  input: { filter?: string; offset?: number; [field: string]: unknown } | undefined,
  filter: RescueDeviceFilter,
): boolean {
  return input?.filter === filter && input.offset === undefined;
}

export interface UseAllRescueDevicesResult {
  /** Every device matching the current filter + search that has been drained so far. */
  devices: Device[];
  /** Server-reported total for the filter (the target the drain fills toward). */
  totalCount: number;
  loading: boolean;
  /**
   * True once every page has drained in, so `devices` is the complete list.
   * False while skipped or still draining — callers must not treat a partial
   * `devices` as the full set (e.g. don't sort/paginate it as if final).
   */
  complete: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export default function useAllRescueDevices(
  filter: RescueDeviceFilter,
  searchQuery: string,
  resellerName: string,
  options?: { skip?: boolean },
): UseAllRescueDevicesResult {
  const skip = options?.skip ?? false;

  const input = useMemo(() => {
    const base: Record<string, unknown> = { filter, limit: DRAIN_PAGE_SIZE };
    if (searchQuery) {
      base.query = searchQuery;
    }
    return base;
  }, [filter, searchQuery]);

  const { data, previousData, loading, error, fetchMore, refetch } = useQuery<RescueDevicesData>(rescueDevicesQuery, {
    variables: { input },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip,
  });

  // While skipped, force the drain gate closed so no fetchMore ever fires.
  const hasMore = skip ? false : (data?.rescueDevices?.hasMore ?? false);
  const itemsLength = data?.rescueDevices?.items.length ?? 0;
  // The drain gate reads the live total: a stale one from `previousData` would let the loop
  // stop early (or run on) against the wrong target while a new search is in flight.
  const drainTarget = data?.rescueDevices?.totalCount ?? 0;

  // The drain is complete once a fresh (non-stale) page has arrived and either
  // the server says there's no more or we've accumulated the full count. Gated
  // on `data` (not `previousData`) so a search transition reads as incomplete.
  const complete = !skip && !!data?.rescueDevices && (!hasMore || itemsLength >= drainTarget);

  const mergeItems = useCallback(
    (previous: RescueDevicesData, fetchMoreResult: RescueDevicesData): RescueDevicesData => ({
      rescueDevices: {
        ...fetchMoreResult.rescueDevices,
        items: [...previous.rescueDevices.items, ...fetchMoreResult.rescueDevices.items],
      },
    }),
    [],
  );

  useDrainAllPages<RescueDevicesData>({
    input,
    hasMore,
    itemsLength,
    totalCount: drainTarget,
    fetchMore,
    mergeItems,
  });

  // Keep the last successful result visible while a new search is in flight
  // (matches the transition strategy in useRescueDevices).
  const effective = data ?? previousData;
  // Reported to callers off `effective`, matching `devices`: a caller rendering stale rows
  // during a search transition would otherwise show them under a count of zero.
  const totalCount = effective?.rescueDevices?.totalCount ?? 0;
  const devices: Device[] = useMemo(() => {
    if (skip) {
      return [];
    }
    const items = effective?.rescueDevices?.items;
    if (!items) {
      return [];
    }
    return items
      .map((item) => mapRescueDeviceItemToDevice(item, resellerName, filter))
      .filter((d): d is Device => d !== null);
  }, [skip, effective?.rescueDevices?.items, resellerName, filter]);

  return {
    devices,
    totalCount,
    loading,
    complete,
    error: error as Error | undefined,
    refetch: () => {
      void refetch();
    },
  };
}
